export interface ScopeUnit {
  id: string;
  labelKey: string;
  /** Interpolation params for the label (e.g. `{ n: 2 }` for "Answer {{n}}"). */
  labelParams?: Record<string, unknown>;
  schemaFields: string[];
  primaryField: string;
}

/** Live array lengths used to enumerate per-item scope units. */
export interface ScopeCounts {
  listAnswers?: number;
  answers?: number;
  questions?: number;
}

const questionTextUnit: ScopeUnit = {
  id: "question",
  labelKey: "question",
  schemaFields: ["question_text"],
  primaryField: "question_text",
};

const explanationUnits: ScopeUnit[] = [
  {
    id: "explanation-true",
    labelKey: "explanationTrue",
    schemaFields: ["explanation_true_text"],
    primaryField: "explanation_true_text",
  },
  {
    id: "explanation-false",
    labelKey: "explanationFalse",
    schemaFields: ["explanation_false_text"],
    primaryField: "explanation_false_text",
  },
];

/** Build one per-item "Answer N" unit for index `i` with the given field addresses. */
const answerUnit = (i: number, schemaFields: string[]): ScopeUnit => ({
  id: `answer-${i}`,
  labelKey: "answerN",
  labelParams: { n: i + 1 },
  schemaFields,
  primaryField: schemaFields[0],
});

const quizz1: ScopeUnit[] = [
  questionTextUnit,
  {
    id: "answer-1",
    labelKey: "answer1",
    schemaFields: ["answer_1_text", "is_answer_1_true"],
    primaryField: "answer_1_text",
  },
  {
    id: "answer-2",
    labelKey: "answer2",
    schemaFields: ["answer_2_text", "is_answer_2_true"],
    primaryField: "answer_2_text",
  },
  {
    id: "answer-3",
    labelKey: "answer3",
    schemaFields: ["answer_3_text", "is_answer_3_true"],
    primaryField: "answer_3_text",
  },
  ...explanationUnits,
];

// content-quizz-2: answers are images (`answer_X_image`) with a correctness
// boolean (`is_answer_X_true`). Neither is meaningful for text AI gen, so
// scope only exposes the question and the two explanations.
const quizz2: ScopeUnit[] = [questionTextUnit, ...explanationUnits];

// content-quizz-3: list_answers (string[]) + correct_answers (boolean[]) are now
// editable & verifiable in the node-form, so each answer is exposed as its own
// per-item scope unit. "Answer N" addresses element N of both parallel arrays;
// the backend index-splice (schemaTransforms.spliceScopedIndices) guarantees
// targeting one answer leaves the others byte-identical.
const quizz3 = (counts?: ScopeCounts): ScopeUnit[] => {
  const n = counts?.listAnswers ?? 0;
  return [
    questionTextUnit,
    ...Array.from({ length: n }, (_, i) =>
      answerUnit(i, [`list_answers[${i}]`, `correct_answers[${i}]`]),
    ),
    ...explanationUnits,
  ];
};

// content-quizz-sort: `answers` is [{ text, index, id_internal }]. Per-item units
// target only the translatable `.text` (index/id_internal are structural).
const quizzSort = (counts?: ScopeCounts): ScopeUnit[] => {
  const n = counts?.answers ?? 0;
  return [
    {
      id: "question",
      labelKey: "question",
      schemaFields: ["question"],
      primaryField: "question",
    },
    ...Array.from({ length: n }, (_, i) => answerUnit(i, [`answers[${i}].text`])),
    ...explanationUnits,
  ];
};

// content-quizz-image-multiple: `list_answers` is [{ text, imageId, is_right }].
// Per-item units target text + correctness (never imageId — system-managed).
const quizzImageMultiple = (counts?: ScopeCounts): ScopeUnit[] => {
  const n = counts?.listAnswers ?? 0;
  return [
    questionTextUnit,
    ...Array.from({ length: n }, (_, i) =>
      answerUnit(i, [`list_answers[${i}].text`, `list_answers[${i}].is_right`]),
    ),
    ...explanationUnits,
  ];
};

// content-random-quizz: `questions` is a doubly-nested array. Per-item units are
// per QUESTION (each addresses the whole nested question object); `end_text` is a
// plain scalar unit.
const randomQuizz = (counts?: ScopeCounts): ScopeUnit[] => {
  const n = counts?.questions ?? 0;
  return [
    {
      id: "end-text",
      labelKey: "endText",
      schemaFields: ["end_text"],
      primaryField: "end_text",
    },
    ...Array.from({ length: n }, (_, i) => ({
      id: `question-${i}`,
      labelKey: "questionN",
      labelParams: { n: i + 1 },
      schemaFields: [`questions[${i}]`],
      primaryField: `questions[${i}]`,
    })),
  ];
};

const yesNo: ScopeUnit[] = [
  {
    id: "question",
    labelKey: "question",
    schemaFields: ["question_text", "is_yes_good_answer"],
    primaryField: "question_text",
  },
  {
    id: "yes-button",
    labelKey: "yesButton",
    schemaFields: ["yes_button_text"],
    primaryField: "yes_button_text",
  },
  {
    id: "no-button",
    labelKey: "noButton",
    schemaFields: ["no_button_text"],
    primaryField: "no_button_text",
  },
  ...explanationUnits,
];

// content-speech-to-check: `keywords_info` is a JSON field edited from
// Spatial Studio only, so it's not exposed as an AI-gen scope unit.
const speechToCheck: ScopeUnit[] = [
  {
    id: "title",
    labelKey: "title",
    schemaFields: ["title"],
    primaryField: "title",
  },
  {
    id: "intro",
    labelKey: "intro",
    schemaFields: ["intro_text"],
    primaryField: "intro_text",
  },
  {
    id: "end",
    labelKey: "endText",
    schemaFields: ["end_text"],
    primaryField: "end_text",
  },
  {
    id: "fail",
    labelKey: "failText",
    schemaFields: ["fail_text"],
    primaryField: "fail_text",
  },
];

const zoneDetect: ScopeUnit[] = [
  {
    id: "title",
    labelKey: "title",
    schemaFields: ["title"],
    primaryField: "title",
  },
  {
    id: "entitled",
    labelKey: "entitled",
    schemaFields: ["entitled"],
    primaryField: "entitled",
  },
  {
    id: "instruction",
    labelKey: "instruction",
    schemaFields: ["instruction"],
    primaryField: "instruction",
  },
];

const elementCatch: ScopeUnit[] = [
  {
    id: "instructions",
    labelKey: "instructions",
    schemaFields: ["text_instructions"],
    primaryField: "text_instructions",
  },
  {
    id: "result",
    labelKey: "result",
    schemaFields: ["text_result"],
    primaryField: "text_result",
  },
];

/** Static scope units, by model — types whose scope never depends on live counts. */
const STATIC_SCOPE_UNITS: Record<string, ScopeUnit[]> = {
  "content-quizz-1": quizz1,
  "content-quizz-2": quizz2,
  "content-yes-no-question": yesNo,
  "content-speech-to-check": speechToCheck,
  "content-zone-detect": zoneDetect,
  "content-element-catch": elementCatch,
  // content-text and content-onboarding: single-field, no scope picker.
  "content-text": [],
  "content-onboarding": [],
};

/** Dynamic scope-unit builders — types with per-item units derived from live counts. */
const DYNAMIC_SCOPE_BUILDERS: Record<string, (counts?: ScopeCounts) => ScopeUnit[]> = {
  "content-quizz-3": quizz3,
  "content-quizz-sort": quizzSort,
  "content-quizz-image-multiple": quizzImageMultiple,
  "content-random-quizz": randomQuizz,
};

export const getScopeUnits = (modelName: string, counts?: ScopeCounts): ScopeUnit[] => {
  const builder = DYNAMIC_SCOPE_BUILDERS[modelName];
  if (builder) return builder(counts);
  return STATIC_SCOPE_UNITS[modelName] ?? [];
};

export const findScopeUnitForField = (
  modelName: string,
  fieldName: string,
): ScopeUnit | undefined => {
  const units = getScopeUnits(modelName);
  return units.find((u) => u.schemaFields.includes(fieldName));
};
